import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import MultiSelectBusca from '../../components/common/MultiSelectBusca';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/usuarios';
import * as clientesApi from '../../api/clientes';
import * as areasApi from '../../api/areas';
import * as empresasApi from '../../api/empresas';

// Esta tela é só para gestor (admin de 1 empresa) e admin — operador não gerencia
// outros usuários (ver get_current_gestor_ou_admin_user no backend).
const PERFIS_SELECIONAVEIS = [
  { value: 'operador', label: 'Operador' },
  { value: 'gestor',   label: 'Gestor' },
  { value: 'cliente',  label: 'Cliente' },
];
const PERFIS_EMPRESA = ['operador', 'gestor'];

function Modal({ item, clientes, onClose, onSaved }) {
  const { user } = useAuth();
  const souGestor = user?.perfil === 'gestor';
  const souAdmin = user?.perfil === 'admin';
  const editando = !!item?.id_usuario_portal;
  const [form, setForm] = useState({
    nome: item?.nome || '',
    email: item?.email || '',
    password: '',
    perfil: item?.perfil || 'cliente',
    id_cliente_final: item?.id_cliente_final || '',
    id_empresa_operadora: item?.id_empresa_operadora || (souAdmin ? (user?.empresa_ativa_id || '') : ''),
  });
  // Perfil cliente: áreas do PRÓPRIO cliente escolhido. Perfil operador: clientes da
  // empresa a que fica restrito (vazio = sem restrição, vê todos).
  const [areasSel, setAreasSel] = useState(item?.areas?.map(a => a.id_area_cliente) || []);
  const [clientesRestritosSel, setClientesRestritosSel] = useState(
    item?.clientes_restritos?.map(c => c.id_cliente_final) || []
  );
  const [areas, setAreas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const toggleArea = (id) => setAreasSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleClienteRestrito = (id) => setClientesRestritosSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  // Empresa explícita só faz sentido pro admin criando/editando operador/gestor
  // (gestor só tem a própria, forçada no backend). Cliente não precisa mais disso —
  // área é ligada direto ao cliente_final escolhido, sem dimensão de empresa.
  const mostrarSeletorEmpresa = souAdmin && PERFIS_EMPRESA.includes(form.perfil);
  // Só admin pode criar/promover outro admin — backend já bloqueia (usuario_service.create/
  // update), aqui é só não oferecer a opção pra quem não pode usá-la (gestor).
  const perfisSelecionaveis = souAdmin
    ? [...PERFIS_SELECIONAVEIS, { value: 'admin', label: 'Admin' }]
    : PERFIS_SELECIONAVEIS;

  useEffect(() => {
    if (souAdmin && PERFIS_EMPRESA.includes(form.perfil)) {
      empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => setEmpresas([]));
    }
  }, [form.perfil, souAdmin]);

  useEffect(() => {
    if (form.perfil !== 'cliente' || !form.id_cliente_final) { setAreas([]); return; }
    areasApi.listar(form.id_cliente_final).then(r => setAreas(r.data)).catch(() => setAreas([]));
  }, [form.perfil, form.id_cliente_final]);

  const handleClienteChange = (v) => {
    setForm(f => ({ ...f, id_cliente_final: v }));
    setAreasSel([]);
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (form.perfil === 'cliente' && !form.id_cliente_final) {
      setErro('Selecione a qual cadastro de cliente este login pertence.');
      return;
    }
    if (mostrarSeletorEmpresa && !form.id_empresa_operadora) {
      setErro('Selecione a empresa.');
      return;
    }
    setErro('');
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (payload.perfil !== 'cliente') delete payload.id_cliente_final;
      if (payload.perfil === 'cliente') payload.areas = areasSel;
      if (payload.perfil === 'operador') payload.clientes_restritos = clientesRestritosSel;
      if (!PERFIS_EMPRESA.includes(payload.perfil)) delete payload.id_empresa_operadora;
      if (souGestor) delete payload.id_empresa_operadora; // backend força a própria empresa do gestor
      if (editando) {
        await api.atualizar(item.id_usuario_portal, payload);
      } else {
        await api.criar(payload);
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  const toggleAtivo = async () => {
    setLoading(true);
    try {
      await api.atualizar(item.id_usuario_portal, { ativo: !item.ativo });
      onSaved();
    } finally { setLoading(false); }
  };

  const clienteOpts = clientes.map(c => ({ value: c.id_cliente_final, label: c.nome }));
  // "Clientes Restritos" só pode listar clientes da MESMA empresa que o operador vai
  // atuar. `clientes` (Modo Global) vem sem filtro de empresa — cliente_final não tem
  // 1 empresa fixa, é vinculado via cliente_final_empresa — então filtra aqui pela
  // empresa escolhida no form (cada item já traz `empresas: [...]` do backend).
  const clienteRestritoOpts = souAdmin
    ? clientes
        .filter(c => form.id_empresa_operadora && (c.empresas || []).some(e => e.id_empresa_operadora === form.id_empresa_operadora && e.ativo))
        .map(c => ({ value: c.id_cliente_final, label: c.nome }))
    : clienteOpts;
  const areaOpts = areas.map(a => ({ value: a.id_area_cliente, label: a.nome }));
  const empresaOpts = empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }));

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{editando ? 'Editar Usuário' : 'Novo Usuário'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}
              <div className="mb-3">
                <label className="form-label fw-semibold">Nome *</label>
                <input name="nome" className="form-control" value={form.nome} onChange={set} required />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">E-mail *</label>
                <input name="email" type="email" className="form-control" value={form.email} onChange={set} required />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Senha {editando && <span className="text-muted fw-normal">(deixe em branco para manter)</span>}
                </label>
                <input name="password" type="password" className="form-control" value={form.password} onChange={set}
                  required={!editando} minLength={6} />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Perfil *</label>
                <select name="perfil" className="form-select" value={form.perfil} onChange={set}>
                  {perfisSelecionaveis.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {form.perfil === 'cliente' && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Cadastro de Cliente Vinculado *</label>
                  <SearchableSelect
                    options={clienteOpts}
                    value={form.id_cliente_final}
                    onChange={handleClienteChange}
                    placeholder="Selecione o cliente (pf/pj)..."
                    required
                  />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Não encontrou? Cadastre primeiro em "Clientes".
                  </div>
                </div>
              )}

              {mostrarSeletorEmpresa && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Empresa Operadora *</label>
                  <SearchableSelect
                    options={empresaOpts}
                    value={form.id_empresa_operadora}
                    onChange={v => { setForm(f => ({ ...f, id_empresa_operadora: v })); setClientesRestritosSel([]); }}
                    placeholder="Selecione a empresa..."
                    required
                  />
                </div>
              )}

              {form.perfil === 'cliente' && (
                <div className="mb-1">
                  <label className="form-label fw-semibold mb-0">Área</label>
                  {areaOpts.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {form.id_cliente_final ? 'Nenhuma área cadastrada para este cliente — cadastre em "Áreas" primeiro.' : 'Selecione o cliente primeiro.'}
                    </div>
                  ) : (
                    <>
                      <MultiSelectBusca
                        options={areaOpts}
                        selected={areasSel}
                        onToggle={toggleArea}
                        placeholder="Buscar área..."
                        vazioLabel="Nenhuma área encontrada."
                      />
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        Sem selecionar nenhuma, o cliente escolhe entre todas as áreas dele ao finalizar um pedido.
                      </div>
                    </>
                  )}
                </div>
              )}

              {form.perfil === 'operador' && (
                <div className="mb-1">
                  <label className="form-label fw-semibold mb-0">Clientes Restritos</label>
                  {clienteRestritoOpts.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {mostrarSeletorEmpresa && !form.id_empresa_operadora
                        ? 'Selecione a empresa primeiro.'
                        : 'Nenhum cliente cadastrado ainda para esta empresa.'}
                    </div>
                  ) : (
                    <>
                      <MultiSelectBusca
                        options={clienteRestritoOpts}
                        selected={clientesRestritosSel}
                        onToggle={toggleClienteRestrito}
                        placeholder="Buscar cliente..."
                        vazioLabel="Nenhum cliente encontrado."
                      />
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        Sem selecionar nenhum, o operador continua vendo/gerenciando todos os clientes da empresa.
                        Selecionando, ele passa a ver só o catálogo e pedidos desses clientes.
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer d-flex justify-content-between">
              {editando ? (
                <button type="button" className={`btn btn-sm ${item.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                  onClick={toggleAtivo} disabled={loading}>
                  {item.ativo ? 'Desativar' : 'Ativar'}
                </button>
              ) : <span />}
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Usuarios() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null);
  const [erroCarregar, setErroCarregar] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([api.listar(), clientesApi.listar()])
      .then(([u, c]) => { setItems(u.data); setClientes(c.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = items.filter(i =>
    i.nome.toLowerCase().includes(busca.toLowerCase()) ||
    i.email.toLowerCase().includes(busca.toLowerCase())
  );

  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  const clienteNome = (id) => clientes.find(c => c.id_cliente_final === id)?.nome || '—';

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Usuários" subtitulo={`${items.length} cadastrados`}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Novo Usuário</button>
      </PageHeader>

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="mb-3">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar por nome ou e-mail..."
          value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <Th campo="nome">Nome</Th>
                  <Th campo="email">E-mail</Th>
                  <Th campo="perfil">Perfil</Th>
                  <th>Cliente</th>
                  <Th campo="ativo">Status</Th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum usuário encontrado.</td></tr>
                )}
                {paginados.map(u => (
                  <tr key={u.id_usuario_portal}>
                    <td className="fw-semibold">{u.nome}</td>
                    <td>{u.email}</td>
                    <td><StatusBadge status={u.perfil} /></td>
                    <td>{u.perfil === 'cliente' ? clienteNome(u.id_cliente_final) : '—'}</td>
                    <td><StatusBadge status={u.ativo ? 'ativo' : 'inativo'} /></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary py-0 px-2"
                        onClick={() => setModal(u)}>Editar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />

      {modal !== null && (
        <Modal item={modal} clientes={clientes}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); carregar(); }} />
      )}
    </div>
  );
}
